import { readJson, storageKey, writeJson } from '../../../lib/storage.js';
import { readGroupState } from './state.js';

/**
 * localStorage group-sync adapter — the offline fallback.
 *
 * Browsers fire a `storage` event in *other* tabs of the same origin whenever a
 * key changes, which is enough to demo group voting across several tabs on one
 * machine without any backend at all.
 *
 * Limitation worth knowing: this cannot reach another device. If a demo needs
 * real phones, Firebase must be configured.
 *
 * @param {string} code
 * @returns {import('./index.js').GroupSync}
 */
export function createLocalSync(code) {
  const key = `session:${code}`;
  const fullKey = storageKey(key);

  const read = () => readGroupState(readJson(key));

  return {
    code,
    isRealtime: false,

    async snapshot() {
      const stored = readJson(key);
      return stored ? readGroupState(stored) : null;
    },

    async join({ memberId, name }) {
      const state = read();
      state.members = { ...state.members, [memberId]: { name, joinedAt: Date.now() } };
      state.votes = { ...state.votes, [memberId]: state.votes[memberId] ?? {} };
      writeJson(key, state);
    },

    async invite(guests) {
      const state = read();
      /** @type {Record<string, {name: string}>} */
      const invites = {};
      for (const guest of guests) invites[guest.id] = { name: guest.name };
      writeJson(key, { ...state, invites });
    },

    async setDeck(selection) {
      writeJson(key, { ...read(), deck: selection });
    },

    subscribe(onState) {
      // Emit current contents immediately so the caller doesn't wait for a change.
      onState(read());

      const onStorage = (event) => {
        if (event.key !== fullKey) return;
        onState(read());
      };

      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    },

    async recordVote({ memberId, restaurantId, direction, time, place }) {
      const state = read();
      if (place && direction === 'right') {
        state.places = { ...state.places, [restaurantId]: place };
      }
      state.votes = {
        ...state.votes,
        [memberId]: { ...(state.votes[memberId] ?? {}), [restaurantId]: direction },
      };
      if (time) {
        state.times = {
          ...state.times,
          [memberId]: { ...(state.times[memberId] ?? {}), [restaurantId]: time },
        };
      }
      writeJson(key, state);
    },

    async submit({ memberId }) {
      const state = read();
      state.submitted = { ...state.submitted, [memberId]: Date.now() };
      writeJson(key, state);
    },

    async end({ memberId }) {
      const state = read();
      if (state.ended) return;
      writeJson(key, { ...state, ended: { at: Date.now(), by: memberId } });
    },

    async leave({ memberId }) {
      const state = read();
      delete state.members[memberId];
      delete state.votes[memberId];
      delete state.times[memberId];
      delete state.submitted[memberId];
      writeJson(key, state);
    },
  };
}
